import { BASE_URL, localizedUrl, normalizeLocale } from "@/shared/lib/seo"
import { toIsoSeoDate } from "@/shared/lib/seo-date"

const HTML_TEXT_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  mdash: "—",
  middot: "·",
  nbsp: " ",
  ndash: "–",
  quot: '"',
  zwj: "\u200d",
}

const decodeCodePoint = (entity: string, code: string, radix: number) => {
  const value = Number.parseInt(code, radix)
  return Number.isSafeInteger(value) && value >= 0 && value <= 0x10ffff
    ? String.fromCodePoint(value)
    : entity
}

const decodeHtmlTextEntities = (value: string) => value
  .replace(/&#x([0-9a-f]+);/gi, (entity, code: string) =>
    decodeCodePoint(entity, code, 16))
  .replace(/&#(\d+);/g, (entity, code: string) =>
    decodeCodePoint(entity, code, 10))
  .replace(/&([a-z]+);/gi, (entity, name: string) =>
    HTML_TEXT_ENTITIES[name.toLowerCase()] ?? entity)

export const stripHtml = (html = "") =>
  decodeHtmlTextEntities(html
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "") // 통짜 문서로 저장된 본문의 head 메타(title 등)가 설명문에 새지 않게
    .replace(/<(style|script|title)[^>]*>[\s\S]*?<\/\1>/gi, "") // style/script/title 블록은 내용까지 제거
    .replace(/<[^>]*>?/gm, "")
  )
    .replace(/\s+/g, " ")
    .trim()

/* 본문 대부분이 "안녕하세요. <지역>에서 진료하는 <이름> 원장입니다." 로 시작한다. 모바일
 * 스니펫은 한글 기준 앞 80자 정도만 보이는데 그 인사말이 35~45자를 먹어, 검색 의도와 맞닿는
 * 증상·시술 서술이 잘려 나간다. 지역·원장명은 title 과 JSON-LD author 가 이미 담고 있어
 * 스니펫에서 한 번 더 쓸 값이 없다.
 *
 * 인사말과 자기소개는 한 문장으로 붙어 오기도 하고(", ... 원장입니다😊") 두 문장으로 나뉘어
 * 오기도 해서 두 경우를 모두 본다. 종결부호 자리에 이모지가 오는 본문이 있어 함께 걷는다. */
const DESCRIPTION_MIN_REMAINDER = 60

const SENTENCE_TAIL = String.raw`(?:[.!?]|\p{Extended_Pictographic}|\s)*`
const GREETING = String.raw`(?:안녕하세요|안녕하십니까|반갑습니다)`
const SPEAKER = String.raw`(?:원장|치과의사|전문의)`

/** 인사말과 자기소개가 한 문장으로 붙어 있는 경우. */
const GREETING_WITH_INTRODUCTION =
  new RegExp(String.raw`^\s*${GREETING}[,\s][^.!?]{0,70}?${SPEAKER}[^.!?]{0,10}?입니다${SENTENCE_TAIL}`, "u")

/** 인사말만 한 문장인 경우. */
const GREETING_SENTENCE =
  new RegExp(String.raw`^\s*${GREETING}[^.!?]{0,20}?[.!?]${SENTENCE_TAIL}`, "u")

/** 자기소개 문장. */
const INTRODUCTION_SENTENCE =
  new RegExp(String.raw`^\s*[^.!?]{0,60}?${SPEAKER}[^.!?]{0,10}?입니다${SENTENCE_TAIL}`, "u")

/** 걷어낸 뒤 남는 글이 너무 짧으면 원문을 그대로 쓴다 — 짧은 본문에서 설명이 비는 것보다 낫다. */
const dropLeading = (text: string, pattern: RegExp) => {
  const matched = pattern.exec(text)
  if (!matched) return text
  const rest = text.slice(matched[0].length)

  return rest.length >= DESCRIPTION_MIN_REMAINDER ? rest : text
}

/** meta description·JSON-LD description 의 원본 텍스트. 본문에서 태그를 걷고,
 * 앞머리의 인사말·자기소개를 덜어낸 뒤 호출부가 필요한 길이만큼 자른다. */
export const toTreatmentCaseDescriptionSource = (html = "") => {
  const text = stripHtml(html)
  const withoutRun = dropLeading(text, GREETING_WITH_INTRODUCTION)
  if (withoutRun !== text) return withoutRun

  return dropLeading(dropLeading(text, GREETING_SENTENCE), INTRODUCTION_SENTENCE)
}

/** 상세 title의 H1과 CMS 본문의 H1이 경쟁하지 않도록 본문 heading만 한 단계 내린다. */
export const normalizeTreatmentCaseBodyHeadings = (html = "") =>
  html.replace(/<(\/?)h1(\b[^>]*)>/gi, "<$1h2$2>")

/* 컨테이너를 :where() 로 감싸 특이도를 0으로 만든다. #medicontent 를 그대로 붙이면 딸림 규칙이
 * medical-content.css 와 특이도가 같아지고, 딸림 <style>이 body 안이라 순서에서 이겨버린다.
 * 실제로 라이브 본문 37건이 body { font-size: 14~16px } 을 갖고 있어 템플릿의 --mc-base 를
 * 덮어썼다. :where() 면 전역 유출은 그대로 막으면서 우선순위는 이 수정 이전과 같게 남는다. */
const MEDICONTENT_SCOPE = ":where(#medicontent)"

/** 문자열 리터럴·괄호·대괄호 안의 콤마는 셀렉터 구분자가 아니다. :is(h1, h2) 나
 * [data-k="a,b"] 를 split(",") 로 쪼개면 셀렉터가 망가져 규칙이 통째로 죽는다. */
const splitTopLevelCommas = (value: string) => {
  const parts: string[] = []
  let depth = 0
  let quote = ""
  let start = 0

  for (let i = 0; i < value.length; i += 1) {
    const char = value[i]
    if (quote) {
      if (char === "\\") i += 1
      else if (char === quote) quote = ""
      continue
    }
    if (char === '"' || char === "'") quote = char
    else if (char === "(" || char === "[") depth += 1
    else if (char === ")" || char === "]") depth = Math.max(0, depth - 1)
    else if (char === "," && depth === 0) {
      parts.push(value.slice(start, i))
      start = i + 1
    }
  }
  parts.push(value.slice(start))

  return parts
}

/** 문자열 리터럴 밖의 첫 "{" 위치. content: "{" 같은 값을 블록 시작으로 오해하지 않는다. */
const findBlockStart = (source: string, from: number) => {
  let quote = ""

  for (let i = from; i < source.length; i += 1) {
    const char = source[i]
    if (quote) {
      if (char === "\\") i += 1
      else if (char === quote) quote = ""
      continue
    }
    if (char === '"' || char === "'") quote = char
    else if (char === "{") return i
  }

  return -1
}

/** open 의 "{" 에 대응하는 "}" 바로 다음 오프셋. 문자열 리터럴 안의 중괄호는 세지 않는다. */
const findBlockEnd = (source: string, open: number) => {
  let depth = 1
  let quote = ""

  for (let i = open + 1; i < source.length; i += 1) {
    const char = source[i]
    if (quote) {
      if (char === "\\") i += 1
      else if (char === quote) quote = ""
      continue
    }
    if (char === '"' || char === "'") quote = char
    else if (char === "{") depth += 1
    else if (char === "}") {
      depth -= 1
      if (depth === 0) return i + 1
    }
  }

  return source.length
}

const scopeEmbeddedCssSelectors = (selectorList: string) =>
  splitTopLevelCommas(selectorList)
    .map((part) => {
      const selector = part.trim()
      if (!selector) return selector
      if (selector === "*") return `${MEDICONTENT_SCOPE} *`
      if (/^(?::root|html|body)$/i.test(selector)) return MEDICONTENT_SCOPE
      return `${MEDICONTENT_SCOPE} ${selector.replace(/^(?::root|html|body)\s+/i, "")}`
    })
    .join(", ")

/** 딸림 CSS의 전역 셀렉터(*, :root, html, body)가 본문 밖 페이지 전체를 덮지 않도록
 * 모든 규칙을 본문 컨테이너 하위로 다시 쓴다. @media/@supports는 내부 규칙만 재귀 처리. */
const scopeEmbeddedCss = (css: string): string => {
  const source = css.replace(/\/\*[\s\S]*?\*\//g, "")
  let result = ""
  let cursor = 0

  while (cursor < source.length) {
    const open = findBlockStart(source, cursor)
    if (open === -1) {
      result += source.slice(cursor)
      break
    }

    const close = findBlockEnd(source, open)
    const rawPrelude = source.slice(cursor, open)
    const body = source.slice(open + 1, close - 1)

    // @import·@charset 처럼 블록 없이 ";" 로 끝나는 at-statement 는 다음 규칙의 prelude 에
    // 붙어 들어온다. 먼저 떼어 그대로 내보내지 않으면 뒤따르는 규칙이 at-rule 로 오인돼
    // 스코프를 못 받고 전역에 남는다.
    const lastSemicolon = rawPrelude.lastIndexOf(";")
    result += rawPrelude.slice(0, lastSemicolon + 1)
    const prelude = rawPrelude.slice(lastSemicolon + 1).trim()

    if (prelude.startsWith("@")) {
      result += /^@(?:media|supports)/i.test(prelude)
        ? `${prelude}{${scopeEmbeddedCss(body)}}`
        : `${prelude}{${body}}`
    } else {
      result += `${scopeEmbeddedCssSelectors(prelude)}{${body}}`
    }

    cursor = close
  }

  return result
}

/** CMS 파이프라인 한 세대가 본문을 완결된 HTML 문서(<html><head>…<body>…)로 저장했다.
 * 그대로 주입하면 페이지마다 <head>/<body>/<title>이 중복돼(SF 기준 45건) 색인 품질을 해치므로
 * 문서 껍데기를 벗겨 fragment로 만든다. 이 본문들은 inline style이 아니라 딸림 <style>에
 * 의존하므로 head 안의 <style>만은 건져서 남기고, 남긴 스타일은 컨테이너 하위로 가둔다. */
export const stripTreatmentCaseDocumentShell = (html = "") =>
  html
    .replace(/<head[^>]*>([\s\S]*?)<\/head>/gi, (_, headInner: string) =>
      (headInner.match(/<style[^>]*>[\s\S]*?<\/style>/gi) ?? []).join(""))
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<\/?(?:html|body)[^>]*>/gi, "")
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<(?:meta|link)[^>]*>/gi, "")
    .replace(/(<style[^>]*>)([\s\S]*?)(<\/style>)/gi, (_, openTag: string, cssText: string, closeTag: string) =>
      `${openTag}${scopeEmbeddedCss(cssText)}${closeTag}`)
    .trim()

/** 본문 주입 직전 최종 정리: 문서 껍데기 제거 → heading 강등. */
export const toTreatmentCaseBodyHtml = (html = "") =>
  normalizeTreatmentCaseBodyHeadings(stripTreatmentCaseDocumentShell(html))

type TreatmentCaseSchemaInput = {
  locale: string
  treatmentCaseId: number
  title: string
  content: string
  treatmentSubjectName?: string
  hospitalOriginName?: string
  hospitalThumbnailUrl?: string
  hospitalFullAddress?: string
  createdAt?: string
  updatedAt?: string
}

export const buildTreatmentCaseSchema = ({
  locale,
  treatmentCaseId,
  title,
  content,
  treatmentSubjectName,
  hospitalOriginName,
  hospitalThumbnailUrl,
  hospitalFullAddress,
  createdAt,
  updatedAt,
}: TreatmentCaseSchemaInput) => {
  const normalizedLocale = normalizeLocale(locale)
  const canonicalUrl = localizedUrl(`treatment-case/${treatmentCaseId}`, normalizedLocale, BASE_URL)
  const description = toTreatmentCaseDescriptionSource(content).slice(0, 160)
  const image = hospitalThumbnailUrl || `${BASE_URL}/icons/logo.svg`
  const datePublished = toIsoSeoDate(createdAt)
  const dateModified = toIsoSeoDate(updatedAt)

  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": `${canonicalUrl}#treatment-case`,
    url: canonicalUrl,
    headline: title,
    description,
    image: [image],
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    mainEntity: treatmentSubjectName
      ? {
        "@type": "MedicalCondition",
        name: treatmentSubjectName,
      }
      : undefined,
    author: hospitalOriginName
      ? {
        "@type": "MedicalOrganization",
        name: hospitalOriginName,
        address: hospitalFullAddress
          ? {
            "@type": "PostalAddress",
            streetAddress: hospitalFullAddress,
            addressCountry: "KR",
          }
          : undefined,
      }
      : undefined,
    publisher: {
      "@id": `${BASE_URL}/#organization`,
    },
  }
}
